import Select from "@components/common/Select";
import { Passenger, Passport } from "@gen/travel/v1/common_pb";
import { Datepicker, Heading, Input } from "@thetreep/ui";
import {
  Control,
  Controller,
  UseFormWatch,
  UseFormTrigger,
  UseFormSetValue,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getNames, registerLocale } from "i18n-iso-countries";
import fr from "i18n-iso-countries/langs/fr.json";
import en from "i18n-iso-countries/langs/en.json";
import i18next from "i18next";
import { useEffect } from "react";

// mandatory to include country locales in the bundle
registerLocale(fr);
registerLocale(en);

interface PassportFormProps {
  index: number;
  passenger: Passenger;
  control: Control<{ passports: Passport[] }>;
  trigger: UseFormTrigger<{ passports: Passport[] }>;
  watch: UseFormWatch<{ passports: Passport[] }>;
  setValue: UseFormSetValue<{ passports: Passport[] }>;
}

function PassportForm({
  index,
  control,
  passenger,
  trigger,
  watch,
  setValue,
}: PassportFormProps) {
  const { t } = useTranslation();

  const issueDateAsString = watch(`passports.${index}.issueDate`);
  const issueDate = stringToDate(issueDateAsString);
  const expiryDateAsString = watch(`passports.${index}.expiryDate`);

  const countries = Object.entries(
    getNames(i18next.language, { select: "official" }),
  ).map(([code, name]) => ({
    value: code,
    label: name,
  }));

  function stringToDate(dateString: string | undefined): Date | undefined {
    if (!dateString) {
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return;
    }
    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(year, month - 1, day); // Months are 0-indexed
    return date;
  }

  function dateToString(date: Date | undefined): string | undefined {
    if (!date) {
      return;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  useEffect(() => {
    if (expiryDateAsString && issueDateAsString) {
      trigger(`passports.${index}.expiryDate`);
    }
  }, [issueDateAsString, expiryDateAsString, trigger, index]);

  return (
    <div className="flex flex-col gap-2 p-8 bg-neutral-50 rounded-sm">
      <Heading>{t("passport.heading", { index: index + 1 })}</Heading>
      <span className="text-grey-blue-500 text-xs">
        {t("passport.mandatoryFields")}
      </span>
      <div className="grid md:grid-cols-2 gap-6 mt-4">
        <div>
          <span className="font-bold text-xs">
            {t("search.passenger.lastname")} *
          </span>
          <Input type="text" value={passenger.lastName} disabled />
        </div>
        <div>
          <span className="font-bold text-xs">
            {t("search.passenger.firstname")} *
          </span>
          <Input type="text" value={passenger.firstName} disabled />
        </div>
        <div>
          <span className="font-bold text-xs">{t("passport.number")} *</span>
          <Controller
            name={`passports.${index}.number`}
            control={control}
            rules={{ required: t("validation.required"), min: 8 }}
            render={({ field, fieldState }) => (
              <Input
                type="text"
                value={field.value || ""}
                onChange={field.onChange}
                error={!!fieldState.error}
                errorMsg={fieldState.error?.message}
                placeholder={t("passport.numberPlaceholder")}
              />
            )}
          />
        </div>

        <div>
          <span className="font-bold text-xs">
            {t("passport.countryOfIssue")} *
          </span>
          <Controller
            name={`passports.${index}.countryOfIssue`}
            control={control}
            rules={{ required: t("validation.required") }}
            render={({ field, fieldState }) => (
              <Select
                options={countries}
                value={field.value}
                onChange={field.onChange}
                search
                error={fieldState.invalid}
                errorMsg={fieldState.error?.message}
              />
            )}
          />
        </div>

        <Controller
          control={control}
          name={`passports.${index}.issueDate`}
          rules={{
            required: t("validation.required"),
          }}
          render={({ field, fieldState }) => (
            <div className="[&_svg]:max-h-4 [&_svg]:mt-1">
              <Datepicker
                placeholder={t("passport.issueDatePlaceholder")}
                value={stringToDate(field.value)}
                onChange={(e) => {
                  const date = dateToString(e.date as Date);

                  setValue(`passports.${index}.issueDate`, date ?? "", {
                    shouldValidate: date !== undefined,
                  });
                }}
                required
                error={fieldState.invalid}
                errorMsg={fieldState?.error?.message}
                label={`${t("passport.issueDate")} *`}
              />
            </div>
          )}
        />

        <Controller
          control={control}
          name={`passports.${index}.expiryDate`}
          rules={{
            required: t("validation.required"),
            validate: (value) => {
              const valueAsDate = stringToDate(value);
              if (valueAsDate && issueDate && valueAsDate < issueDate) {
                return t("passport.validation.expiryDateMustBeAfterIssueDate");
              } else if (valueAsDate && valueAsDate < new Date()) {
                return t("passport.validation.expiryDateMustBeInTheFuture");
              }
              return true;
            },
          }}
          render={({ field, fieldState }) => (
            <div className="[&_svg]:max-h-4 [&_svg]:mt-1">
              <Datepicker
                placeholder={t("passport.expiryDatePlaceholder")}
                value={stringToDate(field.value)}
                onChange={(e) => {
                  const date = dateToString(e.date as Date);

                  setValue(`passports.${index}.expiryDate`, date ?? "", {
                    shouldValidate: date !== undefined,
                  });
                }}
                required
                error={fieldState.invalid}
                errorMsg={fieldState?.error?.message}
                label={`${t("passport.expiryDate")} *`}
                minDate={
                  issueDate && issueDate > new Date() ? issueDate : new Date()
                }
              />
            </div>
          )}
        />
      </div>
    </div>
  );
}

export default PassportForm;
